// src/pages/itsm/ITSMVendors.tsx
import { useState, useEffect } from 'react'
// @ts-ignore
import { supabase } from '../../supabaseClient'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Sun, Moon, User, Mail, Phone, MapPin } from 'lucide-react'
import { useTheme } from '../../hooks/useTheme'
import { T, Card, Btn, Pill, EmptyState, ConfirmDialog, showToast, ToastHost } from '../../components/ui/UI'



export default function ITSMVendors() {
    const navigate = useNavigate()
    const { theme, toggleTheme } = useTheme()
    const [vendors, setVendors] = useState<any[]>([])
    const [showAdd, setShowAdd] = useState(false)
    const [editing, setEditing] = useState<any>(null)
    const [form, setForm] = useState({ name: '', contact_person: '', email: '', phone: '', address: '', category: '' })
    const [confirmDelete, setConfirmDelete] = useState<{ id: number; name: string } | null>(null)

    useEffect(() => { fetchVendors() }, [])

    const fetchVendors = async () => {
        const { data } = await supabase.from('itsm_vendors').select('*').order('name')
        setVendors(data || [])
    }

    const saveVendor = async () => {
        if (editing) {
            await supabase.from('itsm_vendors').update(form).eq('id', editing.id)
            setEditing(null)
        } else {
            await supabase.from('itsm_vendors').insert([form])
        }
        setForm({ name: '', contact_person: '', email: '', phone: '', address: '', category: '' })
        setShowAdd(false)
        fetchVendors()
        showToast('Vendor saved', 'success')
    }

    const deleteVendor = async (id: number) => {
        await supabase.from('itsm_vendors').delete().eq('id', id)
        fetchVendors()
        showToast('Vendor deleted', 'success')
    }

    return (
        <div className="jr-app" data-theme={theme} style={s.page}>
            <button onClick={() => navigate('/dashboard/itsm')} style={s.back}>← Back to ITSM</button>
            <div style={s.header}>
                <div>
                    <h1 style={s.title}>Vendors</h1>
                    <p style={s.subtitle}>{vendors.length} vendors</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ThemeToggle theme={theme} toggleTheme={toggleTheme} />
                    <Btn variant="primary" style={undefined} onClick={() => setShowAdd(true)}>+ Add Vendor</Btn>
                </div>
            </div>

            <div style={s.grid}>
                {vendors.map(v => (
                    <Card key={v.id}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                            <div>
                                <p style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 2 }}>{v.name}</p>
                                {v.category && <Pill tone="info">{v.category}</Pill>}
                            </div>
                            <div style={{ display: 'flex', gap: 6 }}>
                                <Btn size="sm" style={undefined} onClick={() => { setEditing(v); setForm({ name: v.name, contact_person: v.contact_person || '', email: v.email || '', phone: v.phone || '', address: v.address || '', category: v.category || '' }); setShowAdd(true) }}>Edit</Btn>
                                <Btn size="sm" style={{ color: T.danger }} onClick={() => setConfirmDelete({ id: v.id, name: v.name })}>Delete</Btn>
                            </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {v.contact_person && <p style={{ fontSize: 12, color: T.text, display: 'flex', alignItems: 'center', gap: 5 }}><User size={11} />{v.contact_person}</p>}
                            {v.email && <p style={{ fontSize: 12, color: T.accent, display: 'flex', alignItems: 'center', gap: 5 }}><Mail size={11} />{v.email}</p>}
                            {v.phone && <p style={{ fontSize: 12, color: T.text2, display: 'flex', alignItems: 'center', gap: 5 }}><Phone size={11} />{v.phone}</p>}
                            {v.address && <p style={{ fontSize: 12, color: T.text2, display: 'flex', alignItems: 'center', gap: 5 }}><MapPin size={11} />{v.address}</p>}
                        </div>
                    </Card>
                ))}
                {vendors.length === 0 && (
                    <div style={{ gridColumn: 'span 3' }}>
                        <EmptyState title="No vendors yet" sub='Click "+ Add Vendor" to create one' />
                    </div>
                )}
            </div>

            {showAdd && (
                <div style={s.overlay}>
                    <div style={s.modal}>
                        <h2 style={s.modalTitle}>{editing ? 'Edit Vendor' : 'New Vendor'}</h2>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            {[
                                { key: 'name', label: 'Company Name *', placeholder: 'ACME Corp' },
                                { key: 'category', label: 'Category', placeholder: 'IT, Hardware...' },
                                { key: 'contact_person', label: 'Contact Person', placeholder: 'John Doe' },
                                { key: 'email', label: 'Email', placeholder: 'vendor@example.com' },
                                { key: 'phone', label: 'Phone', placeholder: '+966...' },
                                { key: 'address', label: 'Address', placeholder: 'City, Country' },
                            ].map(f => (
                                <div key={f.key} style={s.field}>
                                    <label style={s.label}>{f.label}</label>
                                    <input style={s.input} placeholder={f.placeholder} value={(form as any)[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })} />
                                </div>
                            ))}
                        </div>
                        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                            <Btn style={s.btnOutline} onClick={() => { setShowAdd(false); setEditing(null) }}>Cancel</Btn>
                            <Btn variant="primary" style={undefined} onClick={saveVendor}>Save</Btn>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmDialog
                open={!!confirmDelete}
                onClose={() => setConfirmDelete(null)}
                onConfirm={() => confirmDelete && deleteVendor(confirmDelete.id)}
                title="Delete vendor?"
                message={`This will permanently delete "${confirmDelete?.name}". This cannot be undone.`}
                confirmLabel="Delete"
                danger
            />
            <ToastHost />
        </div>
    )
}

function ThemeToggle({ theme, toggleTheme }: { theme: 'light' | 'dark'; toggleTheme: () => void }) {
    return (
        <button onClick={toggleTheme} aria-label="Toggle theme" style={{
            width: 34, height: 34, borderRadius: T.radiusSm, background: T.surface,
            border: `1px solid ${T.borderStrong}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: T.text2,
        }}>
            {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
        </button>
    )
}

const s: Record<string, React.CSSProperties> = {
    page: { padding: '32px 24px', maxWidth: 1200, margin: '0 auto' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 },
    title: { fontSize: 24, fontWeight: 700, color: T.text, marginBottom: 4 },
    back: { background: 'none', border: 'none', color: T.accent, fontSize: 13, cursor: 'pointer', marginBottom: 20, padding: 0, fontWeight: 500 },
    subtitle: { fontSize: 13, color: T.text2 },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 },
    btnOutline: { flex: 1, background: 'transparent' },
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    modal: { background: T.surface, borderRadius: 12, padding: 28, width: '100%', maxWidth: 520, border: `1px solid ${T.border}` },
    modalTitle: { fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 20 },
    field: { marginBottom: 14 },
    label: { display: 'block', fontSize: 12, fontWeight: 500, color: T.text2, marginBottom: 5 },
    input: { width: '100%', height: 36, border: `0.5px solid ${T.borderStrong}`, borderRadius: 8, padding: '0 10px', fontSize: 13, color: T.text, outline: 'none', background: T.surface },
}
